import React, { ReactNode, useEffect, useMemo } from 'react';
import { ChatMessage } from '@/interface';
import { useDispatch, useSelector } from 'react-redux';
import { authDataSelector } from '@/services/redux/selectors';
import { Text } from '../Text';
import { cn, formatMessageTime } from '@/utils/common_functions';
import { markChatMessagesAsDelivered } from '@/services/redux/slices/chatSlice';
import { BULLET, ChatMessageStatus, ChatMessageType, UserRole } from '@/constants';
import { useSocket } from '@/hooks';
import { updateMessageEvent } from '@/services/socket';
import { Icon } from '../Icon';

interface MessageProps extends ChatMessage {
  consultation_id: number;
}

export const Message: React.FC<MessageProps> = props => {
  const { body, created_at, sender_id, status, id, consultation_id, type } = props;

  // hooks
  const dispatch = useDispatch();
  const { sendMessage } = useSocket();

  // selectors
  const authData = useSelector(authDataSelector);

  // states
  const user_id = authData?.id;
  const isSender = sender_id === user_id;
  const isPatient = authData?.role === UserRole.PATIENT;

  const messageBody = useMemo<ReactNode>(() => {
    if (type === ChatMessageType.DocumentRequest) {
      const points = body.split(BULLET.trim());
      return <div className="flex flex-col gap-1 p-1">
        <div className='flex gap-2 items-center'>
          <Icon className='size-4' type={isSender ? "file_arrow_up_white" : "file_arrow_up_blue"} />
          <Text className=''>Please upload the following reports : </Text>
        </div>
        <ul className='list-disc ml-10'>
          {points.map(point => <li><Text>{point}</Text></li>)}
        </ul>
      </div>
    }
    return <Text variant="body" className="!font-FigtreeMeduim">
      {body}
    </Text>

  }, [body, type]);


  // mark message as delivered
  useEffect(() => {
    if (status === ChatMessageStatus.Sent && sender_id !== user_id) {
      dispatch(markChatMessagesAsDelivered({
        consultation_id,
        message_id: id,
      }));

      // emit event to server
      sendMessage(updateMessageEvent(id, { status: ChatMessageStatus.Delivered }));
    }
  }, [status, id]);



  return (
    <>
      <div className={cn('flex flex-col gap-2', isSender ? 'items-end' : 'items-start')}>
        <div className={cn(
          "max-w-[90%] py-2 px-3 rounded-xl",
          isSender ? 'bg-gradient-to-r from-ui_blue to-ui_blue_light rounded-br-none text-white' : 'bg-[#EDEFF5] rounded-bl-none text-ui_blue')}>
          {messageBody}
        </div>

        {/* extra message to be appended for document request */}
        {isPatient && type === ChatMessageType.DocumentRequest && !isSender && <div className={cn(
          "max-w-[90%] py-2 px-3 rounded-xl",
          isSender ? 'bg-gradient-to-r from-ui_blue to-ui_blue_light rounded-br-none text-white' : 'bg-[#EDEFF5] rounded-bl-none text-ui_blue')}>
          <Text variant="body">
            You can upload them using <span className='font-FigtreeBold italic'>'Upload additional document'</span> button.
          </Text>
        </div>}

        {/* time */}
        <Text variant="tiny" className="!font-FigtreeMeduim text-ui_placeholder">
          {formatMessageTime(created_at)}
        </Text>
      </div>

    </>
  );
};
